import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { MailCheck, Loader2 } from 'lucide-react';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/store/useAuthStore';
import { toast } from 'sonner';

function extractError(err: unknown): string {
  const msg =
    (err as { response?: { data?: { message?: string | string[] } } })?.response?.data
      ?.message ||
    (err as Error)?.message ||
    'Code invalide ou expiré.';
  return Array.isArray(msg) ? msg.join(', ') : msg;
}

/**
 * Page de confirmation par code OTP : saisie manuelle de l'email + du code à 6
 * chiffres reçu par email. Sert de porte de secours si l'utilisateur a fermé la
 * fenêtre d'inscription avant d'avoir saisi son code.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const applySession = useAuthStore((s) => s.applySession);
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [code, setCode] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || code.length !== 6) return;
    setProcessing(true);
    try {
      const { data } = await authApi.verifyEmail(email.trim(), code);
      applySession(
        data.access_token,
        data.user as { name: string; email: string; role?: 'user' | 'admin' },
      );
      toast.success('Email confirmé');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setProcessing(false);
    }
  };

  const handleResend = async () => {
    if (!email.trim()) {
      toast.error('Renseignez votre adresse email.');
      return;
    }
    setProcessing(true);
    try {
      await authApi.resendVerification(email.trim());
      toast.success('Nouveau code envoyé.');
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-app flex flex-col items-center justify-center p-6 text-center">
      <div className="w-full max-w-md bg-overlay-1 border border-line rounded-2xl p-8">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
          <MailCheck className="w-8 h-8 text-white" />
        </div>

        <h1 className="text-xl font-bold text-fg mb-2">Confirmez votre email</h1>
        <p className="text-sm text-fg-subtle leading-relaxed mb-6">
          Saisissez le code à 6 chiffres que nous vous avons envoyé par email.
        </p>

        <form onSubmit={handleSubmit} className="space-y-3 text-left">
          <input
            type="email"
            placeholder="votre@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 bg-overlay-2 border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-sm"
          />
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full py-3 bg-overlay-2 border border-line text-fg rounded-xl focus:outline-none focus:border-green-500/40 placeholder-fg-faint text-center text-2xl font-semibold tracking-[0.4em]"
          />
          <button
            type="submit"
            disabled={processing || !email.trim() || code.length !== 6}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white py-3.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-60"
          >
            {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Activer mon compte'}
          </button>
        </form>

        <button
          onClick={handleResend}
          disabled={processing}
          className="w-full mt-3 py-3 bg-overlay-2 border border-line rounded-xl text-sm text-fg-muted hover:bg-overlay-3 transition-colors disabled:opacity-60"
        >
          Je n'ai rien reçu — renvoyer le code
        </button>

        <Link
          to="/dashboard"
          className="inline-block mt-4 text-xs text-fg-faint hover:text-fg-subtle transition-colors"
        >
          Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}
